import * as d3 from 'd3';
import { MarketAnalysisData, SeriesVisibility } from '../../types';
import { RenderContext } from '../types';
import { ANALYSIS_COLORS } from '../constants';
import { detectFrequency } from '../../candleUtils';
import { calculateBarDimensions } from '../utils/barDimensions';

interface FullSeriesModeConfig {
    analysisData: MarketAnalysisData;
    visibility: SeriesVisibility;
}

export function renderFullSeriesMode(ctx: RenderContext, config: FullSeriesModeConfig): void {
    const { analysisData, visibility } = config;

    renderUncertaintyBand(ctx, analysisData, visibility);
    renderCandles(ctx, analysisData, visibility);
    renderBidLine(ctx, analysisData, visibility);
    renderAskLine(ctx, analysisData, visibility);
    renderFairValueLine(ctx, analysisData, visibility);
    renderTradeMarkers(ctx, analysisData, visibility);
}

function renderUncertaintyBand(
    ctx: RenderContext,
    analysisData: MarketAnalysisData,
    visibility: SeriesVisibility
): void {
    if (analysisData.fairValue.length === 0 || !visibility.uncertaintyBand) return;

    const { g, scales } = ctx;

    const areaGenerator = d3.area<{ timestamp: Date; upperBound: number; lowerBound: number }>()
        .x(d => scales.x(d.timestamp))
        .y0(d => scales.y(d.lowerBound))
        .y1(d => scales.y(d.upperBound))
        .curve(d3.curveMonotoneX);

    g.append('path')
        .datum(analysisData.fairValue)
        .attr('class', 'uncertainty-band')
        .attr('fill', ANALYSIS_COLORS.uncertaintyBand)
        .attr('stroke', 'none')
        .attr('d', areaGenerator)
        .style('pointer-events', 'none');
}

function renderCandles(
    ctx: RenderContext,
    analysisData: MarketAnalysisData,
    visibility: SeriesVisibility
): void {
    if (!analysisData.candles || analysisData.candles.length === 0 || !visibility.candles) return;

    const { g, scales, layout, clipId } = ctx;

    // Calculate candle dimensions based on time duration using x-scale
    const frequencyMs = ctx.candleFrequencyMs ?? detectFrequency(analysisData.candles).frequencyMs;
    const { widthFromScale: candleWidthFromScale, width: candleWidth, offset: candleOffset } = calculateBarDimensions(scales.x, frequencyMs);

    // Create a clipped group for candles
    const candleGroup = g.append('g')
        .attr('class', 'candle-group')
        .attr('clip-path', `url(#${clipId})`);

    analysisData.candles.forEach(candle => {
        const xPos = scales.x(candle.timestamp);
        // Skip candles completely outside visible area
        if (xPos + candleWidthFromScale < 0 || xPos > layout.innerWidth) return;

        const isUp = candle.close >= candle.open;
        const color = isUp ? ANALYSIS_COLORS.candleUp : ANALYSIS_COLORS.candleDown;

        // Draw wick (high-low line)
        candleGroup.append('line')
            .attr('class', 'candle-wick')
            .attr('x1', xPos + candleWidthFromScale / 2)
            .attr('x2', xPos + candleWidthFromScale / 2)
            .attr('y1', scales.y(candle.high))
            .attr('y2', scales.y(candle.low))
            .attr('stroke', color)
            .attr('stroke-width', Math.max(1, candleWidth / 8))
            .attr('opacity', candle.isFilled ? 0.3 : 1)
            .style('pointer-events', 'none');

        // Draw body (open-close rect)
        const bodyTop = scales.y(Math.max(candle.open, candle.close));
        const bodyBottom = scales.y(Math.min(candle.open, candle.close));
        const bodyHeight = Math.max(1, bodyBottom - bodyTop);

        candleGroup.append('rect')
            .attr('class', 'candle-body')
            .attr('x', xPos + candleOffset)
            .attr('y', bodyTop)
            .attr('width', candleWidth)
            .attr('height', bodyHeight)
            .attr('fill', color)
            .attr('stroke', color)
            .attr('stroke-width', 0.5)
            .attr('opacity', candle.isFilled ? 0.3 : 1)
            .style('pointer-events', 'none');
    });
}

function renderBidLine(
    ctx: RenderContext,
    analysisData: MarketAnalysisData,
    visibility: SeriesVisibility
): void {
    if (analysisData.bidAsk.length === 0 || !visibility.bidLine) return;

    const { g, scales } = ctx;

    const bidLine = d3.line<{ timestamp: Date; bidPrice: number }>()
        .x(d => scales.x(d.timestamp))
        .y(d => scales.y(d.bidPrice))
        .curve(d3.curveMonotoneX);

    g.append('path')
        .datum(analysisData.bidAsk)
        .attr('class', 'bid-line')
        .attr('fill', 'none')
        .attr('stroke', ANALYSIS_COLORS.bid)
        .attr('stroke-width', 1.5)
        .attr('d', bidLine)
        .style('pointer-events', 'none');
}

function renderAskLine(
    ctx: RenderContext,
    analysisData: MarketAnalysisData,
    visibility: SeriesVisibility
): void {
    if (analysisData.bidAsk.length === 0 || !visibility.askLine) return;

    const { g, scales } = ctx;

    const askLine = d3.line<{ timestamp: Date; askPrice: number }>()
        .x(d => scales.x(d.timestamp))
        .y(d => scales.y(d.askPrice))
        .curve(d3.curveMonotoneX);

    g.append('path')
        .datum(analysisData.bidAsk)
        .attr('class', 'ask-line')
        .attr('fill', 'none')
        .attr('stroke', ANALYSIS_COLORS.ask)
        .attr('stroke-width', 1.5)
        .attr('d', askLine)
        .style('pointer-events', 'none');
}

function renderFairValueLine(
    ctx: RenderContext,
    analysisData: MarketAnalysisData,
    visibility: SeriesVisibility
): void {
    if (analysisData.fairValue.length === 0 || !visibility.fairValueLine) return;

    const { g, scales } = ctx;

    const fairValueLine = d3.line<{ timestamp: Date; probability: number }>()
        .x(d => scales.x(d.timestamp))
        .y(d => scales.y(d.probability))
        .curve(d3.curveMonotoneX);

    g.append('path')
        .datum(analysisData.fairValue)
        .attr('class', 'fair-value-line')
        .attr('fill', 'none')
        .attr('stroke', ANALYSIS_COLORS.fairValue)
        .attr('stroke-width', 2)
        .attr('d', fairValueLine)
        .style('pointer-events', 'none');
}

function renderTradeMarkers(
    ctx: RenderContext,
    analysisData: MarketAnalysisData,
    visibility: SeriesVisibility
): void {
    if (analysisData.trades.length === 0 || !visibility.trades) return;

    const { g, scales, layout, theme } = ctx;

    const triangleSize = 4;
    const baseSymbolSize = triangleSize * triangleSize * 3;

    analysisData.trades.forEach(trade => {
        const xPos = scales.x(trade.timestamp);
        const yPos = scales.y(trade.price);

        // Skip if outside visible range
        if (xPos < 0 || xPos > layout.innerWidth) return;

        const isBuy = trade.side === 'buy';
        const color = isBuy ? ANALYSIS_COLORS.buyTrade : ANALYSIS_COLORS.sellTrade;
        const rotation = isBuy ? 0 : 180;
        const yOffset = isBuy ? 8 : -8;

        g.append('path')
            .attr('class', 'trade-marker')
            .attr('id', `trade-marker-${trade.transactionId}`)
            .attr('data-base-size', baseSymbolSize)
            .attr('d', d3.symbol().type(d3.symbolTriangle).size(baseSymbolSize)())
            .attr('transform', `translate(${xPos}, ${yPos + yOffset}) rotate(${rotation})`)
            .attr('fill', color)
            .attr('stroke', theme.palette.background.paper)
            .attr('stroke-width', 0.5)
            .style('pointer-events', 'none');
    });
}
